import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { Skeleton } from 'antd'
import { getCubeAPI } from '../../../../../../Cube'
import { QueryRenderer, QueryRendererRenderProps } from '@cubejs-client/react'
import { ReportFilterStore } from '../../ReportFilterStore'
import { parseAndDownloadCSV, GrPrice } from '../../../../../../utilities/helpers'
import { ICubeQuery } from '@getgreenline/shared'

type QueryRendererRenderProps = typeof QueryRendererRenderProps

interface CombinedData {
  id: number | null
  name: string
  amount: number
}

enum INVOICES_SCHEMAS {
  INVOICES = 'Invoices',
  INVOICE_FEES_TAXES = 'Invoices_InvoiceFees',
  INVOICE_FEES = 'InvoiceFees',
}

interface CubeRawLinesTaxesDataItem {
  'Taxes.id': number
  'Taxes.name': string
  'Invoices.invoiceLinesTaxes': number
}

interface CubeRawFeesTaxesDataItem {
  'Taxes.id': number
  'Taxes.name': string
  'Invoices_InvoiceFees.invoiceFeesTaxes': number
}

interface CubeRawFeesDataItem {
  'InvoiceFees.name': string
  'Invoices.feesTotal': number
}

interface Props {
  companyId: number
  reportFilterStore?: ReportFilterStore
}

interface State {
  linesTaxesQuery?: Omit<ICubeQuery, 'order' | 'limit'>
  feesTaxesQuery?: Omit<ICubeQuery, 'order' | 'limit'>
  feesQuery?: Omit<ICubeQuery, 'order' | 'limit'>
  linesTaxesData?: CubeRawLinesTaxesDataItem[]
  feesTaxesData?: CubeRawFeesTaxesDataItem[]
  feesData?: CubeRawFeesDataItem[]
}

@inject('reportFilterStore')
@observer
export class Taxes extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      linesTaxesQuery: undefined,
      feesTaxesQuery: undefined,
      feesQuery: undefined,
      linesTaxesData: undefined,
      feesTaxesData: undefined,
      feesData: undefined,
    }
  }

  componentDidMount = () => {
    this.setState({
      linesTaxesQuery: this.getQuery(INVOICES_SCHEMAS.INVOICES),
      feesTaxesQuery: this.getQuery(INVOICES_SCHEMAS.INVOICE_FEES_TAXES),
      feesQuery: this.getFeesQuery(),
    })
  }

  getQuery = (schema: INVOICES_SCHEMAS): Omit<ICubeQuery, 'order' | 'limit'> | undefined => {
    const reportFilterStore = this.props.reportFilterStore

    if (!reportFilterStore) {
      return
    }

    let measures

    if (schema === INVOICES_SCHEMAS.INVOICES) {
      measures = [`${schema}.invoiceLinesTaxes`]
    } else {
      measures = [`${schema}.invoiceFeesTaxes`]
    }

    return {
      measures,
      timeDimensions: [
        {
          dimension: `${schema}.completedate`,
          granularity: null,
          dateRange: [reportFilterStore.startDateString, reportFilterStore.endDateString],
        },
      ],
      dimensions: ['Taxes.name', `Taxes.id`],
      filters: [
        {
          dimension: `${schema}.status`,
          operator: 'equals',
          values: [`completed`],
        },
        {
          dimension: `${schema}.locationId`,
          operator: 'equals',
          values: reportFilterStore.selectedLocationIds.map((id) => `${id}`),
        },
      ],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
  }

  getFeesQuery = (): Omit<ICubeQuery, 'order' | 'limit'> | undefined => {
    const reportFilterStore = this.props.reportFilterStore

    if (!reportFilterStore) {
      return
    }

    return {
      measures: [`${INVOICES_SCHEMAS.INVOICES}.feesTotal`],
      timeDimensions: [
        {
          dimension: `${INVOICES_SCHEMAS.INVOICES}.completedate`,
          granularity: null,
          dateRange: [reportFilterStore.startDateString, reportFilterStore.endDateString],
        },
      ],
      dimensions: [`${INVOICES_SCHEMAS.INVOICE_FEES}.name`],
      filters: [
        {
          dimension: `${INVOICES_SCHEMAS.INVOICES}.status`,
          operator: 'equals',
          values: [`completed`],
        },
        {
          dimension: `${INVOICES_SCHEMAS.INVOICES}.locationId`,
          operator: 'equals',
          values: reportFilterStore.selectedLocationIds.map((id) => `${id}`),
        },
      ],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
  }

  fetchAndSetData(dataKey: 'linesTaxesData' | 'feesTaxesData' | 'feesData') {
    return ({ resultSet, error, loadingState: { isLoading } }: QueryRendererRenderProps) => {
      if (isLoading) {
        return <Skeleton active />
      }

      if (error) {
        return error.toString()
      }

      if (resultSet && !this.state[dataKey]) {
        this.setState({ [dataKey]: resultSet.rawData() })
      } else if (!resultSet && this.state[dataKey]) {
        this.setState({ [dataKey]: undefined })
      }

      return null
    }
  }

  get csvFormattedData() {
    return this.combinedData.map((item) => ({
      'Tax Id': item.id,
      Name: item.name,
      Amount: item.amount,
    }))
  }

  get combinedData() {
    const { linesTaxesData, feesTaxesData, feesData } = this.state

    if (!linesTaxesData || !feesTaxesData || !feesData) {
      return []
    }

    const result: CombinedData[] = []

    for (const lineTax of linesTaxesData) {
      const taxId = lineTax['Taxes.id']
      let amount = lineTax['Invoices.invoiceLinesTaxes']
      const feeTax = feesTaxesData.find((fee) => fee['Taxes.id'] === taxId)

      if (feeTax) {
        amount += feeTax['Invoices_InvoiceFees.invoiceFeesTaxes']
      }

      result.push({
        id: taxId,
        name: lineTax['Taxes.name'],
        amount,
      })
    }

    for (const fee of feesData) {
      result.push({
        id: null,
        name: fee['InvoiceFees.name'],
        amount: fee['Invoices.feesTotal'],
      })
    }

    // The taxes queries are returning 1 empty row which needs to be filtered out
    return result.filter((item) => item.name && item.amount !== 0)
  }

  renderTable = () => {
    if (this.combinedData.length === 0) {
      return <div>No data</div>
    }

    return (
      <table className='table table-sm'>
        <tbody>
          {this.combinedData.map((data, index) => {
            return (
              <tr key={index}>
                <td>{data.name}</td>
                <td className='text-right'>{GrPrice.convertDollarToDollar(data.amount)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  exportCSV() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const data = this.csvFormattedData
    if (data) {
      parseAndDownloadCSV(`purchase-order-vendors-${startDate}-${endDate}`, data)
    }
  }

  render() {
    const { linesTaxesQuery, feesTaxesQuery, linesTaxesData, feesTaxesData, feesQuery, feesData } =
      this.state

    return (
      <>
        <div className='rounded background-white'>
          <div className='p-3'>
            <div>
              <b>Taxes and Fees</b>
            </div>
            <div className='text-muted'>
              Attach your purchase orders to a tax group to use this report
            </div>

            <div className='mt-2 mb-2'>
              <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
                Export as csv
              </button>
            </div>

            <div className='mt-2'>
              {linesTaxesQuery && feesTaxesQuery && feesQuery ? (
                <>
                  <QueryRenderer
                    query={linesTaxesQuery}
                    cubejsApi={getCubeAPI(this.props.companyId)}
                    render={this.fetchAndSetData('linesTaxesData')}
                  />
                  <QueryRenderer
                    query={feesTaxesQuery}
                    cubejsApi={getCubeAPI(this.props.companyId)}
                    render={this.fetchAndSetData('feesTaxesData')}
                  />
                  <QueryRenderer
                    query={feesQuery}
                    cubejsApi={getCubeAPI(this.props.companyId)}
                    render={this.fetchAndSetData('feesData')}
                  />
                </>
              ) : (
                <Skeleton active />
              )}
              {linesTaxesData && feesTaxesData && feesData && this.renderTable()}
            </div>
          </div>
        </div>
      </>
    )
  }
}
